/**
 * Marketing entry point — lightweight alternative to index.tsx.
 *
 * This entry is used for public/marketing pages (/, /pricing, /blog, /score, etc.).
 * It does NOT load firebase, AuthProvider, SubscriptionProvider, Sentry, or
 * react-query, producing a much smaller initial JS payload (~100 KB vs ~500 KB).
 *
 * The full app entry (index.tsx) is used for authenticated routes (/login,
 * /dashboard, /onboarding) and is loaded via full-page navigation from CTAs.
 */
import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import { StyledEngineProvider } from '@mui/material/styles';
import { HelmetProvider } from 'react-helmet-async';
import MarketingApp from './app-marketing';
import { installDomNodeGuard } from './utils/domNodeGuard';
import { isChunkError } from './utils/chunkErrorReload';

// Neutralise les crashs removeChild/insertBefore causés par les mutations
// DOM externes (Google Translate, extensions navigateur). À installer avant
// tout rendu React. Voir src/utils/domNodeGuard.ts.
installDomNodeGuard();

// Auto-reload on dynamic import failure (stale chunks after deploy).
// Détection centralisée via isChunkError (couvre Chrome "Failed to fetch…",
// Firefox "error loading…", Safari MIME type) — évite toute divergence de
// pattern entre bundles.
const reloadOnStaleChunk = (msg: string): void => {
  if (!isChunkError(msg)) return;
  const key = 'fitd-chunk-reload';
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, '1');
    window.location.reload();
  }
};
window.addEventListener('error', (event) => {
  reloadOnStaleChunk(event.error?.message || event.message || '');
});
window.addEventListener('unhandledrejection', (event) => {
  reloadOnStaleChunk(event.reason?.message || String(event.reason ?? ''));
});
sessionStorage.removeItem('fitd-chunk-reload');

const rootElement = document.getElementById('root')!;

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <HelmetProvider>
      <StyledEngineProvider injectFirst>
        <MarketingApp />
      </StyledEngineProvider>
    </HelmetProvider>
  </React.StrictMode>,
);
